import { useEffect, useState, useCallback } from "react";
import api, { downloadBlob, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { ListChecks, DownloadSimple, Lock, Trash, ArrowsClockwise } from "@phosphor-icons/react";

export const AuditTrailPanel = () => {
  const [logs, setLogs] = useState([]);
  const [status, setStatus] = useState({ count: 0, clear_enabled: false });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearText, setClearText] = useState("");
  const [clearing, setClearing] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.get("/audit-logs"), api.get("/audit-logs/status")])
      .then(([l, s]) => { setLogs(l.data || []); setStatus(s.data || { count: 0, clear_enabled: false }); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const doExport = async () => {
    setExporting(true);
    try { await downloadBlob("/audit-logs/export", `claro_audit_trail_${new Date().toISOString().slice(0, 10)}.xlsx`); }
    catch (e) { toast.error(e.message || "Export failed."); }
    finally { setExporting(false); }
  };

  const doClear = async () => {
    setClearing(true);
    try {
      const r = await api.post("/audit-logs/clear", { confirm: "CLEAR" });
      toast.success(`Cleared ${r.data.cleared} events.`);
      setConfirmClear(false); setClearText(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setClearing(false); }
  };

  return (
    <div data-testid="console-audit-trail" className="border border-border bg-card">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-secondary/40">
        <div className="flex items-center gap-2">
          <ListChecks size={18} weight="duotone" />
          <h3 className="font-semibold text-foreground">Platform Audit Trail</h3>
          <span className="text-xs text-muted-foreground">({status.count} events · showing latest {logs.length})</span>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="rounded-none" onClick={load} data-testid="audit-refresh">
            <ArrowsClockwise size={14} className="mr-1" /> Refresh
          </Button>
          <Button size="sm" className="rounded-none" onClick={doExport} disabled={exporting} data-testid="audit-export-btn">
            <DownloadSimple size={14} className="mr-1" /> {exporting ? "…" : "Export Excel"}
          </Button>
        </div>
      </div>

      {!status.clear_enabled ? (
        <div className="flex items-center gap-2 px-4 py-2 text-xs text-muted-foreground bg-muted/40 border-b border-border">
          <Lock size={13} /> Append-only. Clearing is disabled to preserve the SOC 2 retention record
          (set <code className="mx-1">ALLOW_AUDIT_CLEAR=true</code> for a one-time reset).
        </div>
      ) : (
        <div className="flex items-center justify-between px-4 py-2 bg-amber-50 border-b border-amber-200">
          <span className="text-xs text-amber-800">Clearing is currently ENABLED (ALLOW_AUDIT_CLEAR=true). Disable it after any reset.</span>
          {!confirmClear ? (
            <Button size="sm" variant="outline" className="rounded-none text-destructive border-destructive/40 hover:bg-destructive/10"
                    onClick={() => setConfirmClear(true)} data-testid="audit-clear-btn">
              <Trash size={14} className="mr-1" /> Clear trail
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <Input value={clearText} onChange={(e) => setClearText(e.target.value)} placeholder="Type CLEAR"
                     className="h-8 w-32 rounded-none" data-testid="audit-clear-input" />
              <Button size="sm" className="rounded-none bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      disabled={clearText !== "CLEAR" || clearing} onClick={doClear} data-testid="audit-clear-confirm">
                {clearing ? "…" : "Confirm"}
              </Button>
              <Button size="sm" variant="ghost" className="rounded-none" onClick={() => { setConfirmClear(false); setClearText(""); }}>Cancel</Button>
            </div>
          )}
        </div>
      )}

      <div className="max-h-[55vh] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0">
            <tr className="border-b border-border bg-secondary/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-4 py-2">Time (UTC)</th><th className="px-4 py-2">User</th>
              <th className="px-4 py-2">Action</th><th className="px-4 py-2">Entity</th><th className="px-4 py-2">Details</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="px-4 py-6 text-muted-foreground text-center">Loading…</td></tr>}
            {!loading && logs.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-muted-foreground text-center">No audit events yet.</td></tr>}
            {logs.map((l) => (
              <tr key={l.id} data-testid="audit-row" className="border-b border-border">
                <td className="px-4 py-2 font-mono text-xs">{l.timestamp?.slice(0, 19).replace("T", " ")}</td>
                <td className="px-4 py-2">{l.user_name}{l.is_superadmin ? " ★" : ""}</td>
                <td className="px-4 py-2 capitalize">{(l.action || "").replace(/_/g, " ")}</td>
                <td className="px-4 py-2 capitalize">{l.entity_type}</td>
                <td className="px-4 py-2 text-muted-foreground">{l.details}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AuditTrailPanel;
